import React, { Suspense, lazy, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from './services';
import type { Partner, AppSettings } from './services/types';
import { DEFAULT_CARD_IMAGE, CITIES, DEFAULT_LOGIN_ONBOARDING_SLIDES } from './constants';
import { ToastProvider } from './hooks/useToast';
import { useFavorites } from './hooks/useFavorites';
import {
  getPartnerIdFromPath,
  getPartnerIdFromPublicPath,
  isKnownPath,
  partnerDetailPath,
  pathToTab,
  tabToPath,
  type AppTab,
} from './routes/paths';

const loadHomePage = () => import('./pages/Home');
const PartnerJoin = lazy(() =>
  import('./pages/PartnerJoin').then((module) => ({ default: module.PartnerJoin })),
);
const Home = lazy(() => loadHomePage().then((module) => ({ default: module.Home })));
const PublicPartnerPage = lazy(() =>
  import('./pages/PublicPartnerPage').then((module) => ({ default: module.PublicPartnerPage })),
);
const Favorites = lazy(() => loadFavoritesPage().then((module) => ({ default: module.Favorites })));
const Profile = lazy(() => loadProfilePage().then((module) => ({ default: module.Profile })));

import { BottomNav } from './components/BottomNav';
import { LoadingScreen } from './components/LoadingScreen';
import {
  AdminSkeleton,
  ExploreSkeleton,
  FavoritesSkeleton,
  HomeSkeleton,
  ProfileSkeleton,
  ScannerSkeleton,
} from './components/AppSkeletons';
import { perfEnd, perfLogOnce, perfStart } from './utils/perf';
import { readPublicCache, writePublicCache } from './utils/publicCache';
import { isSixyAIEnabled } from './utils/featureFlags';
import { UpdatePrompt } from './components/UpdatePrompt';
import { InstallBanner } from './components/InstallBanner';
import { OfflineBanner } from './components/OfflineBanner';
import { AvatarOnboarding } from './components/AvatarOnboarding';
import { DailyBonusModal } from './components/DailyBonusModal';
import { PushPromptAfterCheckin } from './components/PushPromptAfterCheckin';
import { GuidedTour } from './components/GuidedTour';
import { ConciergeProvider } from './components/SixyConcierge';
import { setSentryUser } from './utils/sentry';
import { setAnalyticsUser, trackEvent } from './utils/analytics';
import { setAuditContext } from './utils/auditLog';
import { useDeviceDataAttributes } from './hooks/useDevice';
import { useDynamicPerfTier } from './hooks/usePerfTier';

import { useAuth } from './contexts/AuthContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { LoginFlow } from './pages/LoginFlow';

const loadFavoritesPage = () => import('./pages/Favorites');
const loadProfilePage = () => import('./pages/Profile');
const loadExplorePage = () => import('./pages/Explore');
const loadAdminPage = () => import('./pages/Admin');
const loadScannerPage = () => import('./pages/Scanner');
const loadCompanyDashboardPage = () => import('./pages/CompanyDashboard');
const loadPartnerModal = () => import('./components/PartnerModal');
const loadCitySelectorModal = () => import('./components/CitySelectorModal');

const Explore = lazy(() => loadExplorePage().then((module) => ({ default: module.Explore })));
const Admin = lazy(() => loadAdminPage().then((module) => ({ default: module.Admin })));
const Scanner = lazy(() => loadScannerPage().then((module) => ({ default: module.Scanner })));
const CompanyDashboard = lazy(() =>
  loadCompanyDashboardPage().then((module) => ({ default: module.CompanyDashboard })),
);
const PrivacyPolicy = lazy(() =>
  import('./pages/legal/PrivacyPolicy').then((module) => ({ default: module.PrivacyPolicy })),
);
const TermsOfService = lazy(() =>
  import('./pages/legal/TermsOfService').then((module) => ({ default: module.TermsOfService })),
);
const NotFound = lazy(() =>
  import('./pages/NotFound').then((module) => ({ default: module.NotFound })),
);

const PARTNERS_CACHE_KEY = '@sixpass:cache:partners';
const SETTINGS_CACHE_KEY = '@sixpass:cache:settings';
const PUBLIC_CACHE_MAX_AGE_MS = 1000 * 60 * 15;
const PartnerModal = lazy(() =>
  loadPartnerModal().then((module) => ({ default: module.PartnerModal })),
);
const CitySelectorModal = lazy(() =>
  loadCitySelectorModal().then((module) => ({ default: module.CitySelectorModal })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Janela durante a qual um partner_viewed por sessionStorage não é re-registrado
const PARTNER_VIEW_SESSION_WINDOW_MS = 1000 * 60 * 30;

function AppContent() {
  const MIN_SPLASH_DURATION_MS = 520;
  const SPLASH_EXIT_BUFFER_MS = 90;
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  // Injeta data-* no <html> baseado em device, tamanho, plataforma e modo PWA.
  // Componentes filhos podem estilizar via `[data-device-size='small'] ...`
  // ou consumir via `useDevice()` direto.
  useDeviceDataAttributes();
  // Mantém data-perf-tier dinâmico baseado em rede + bateria + hardware
  // (hardware inicial já foi setado em main.tsx via applyPerfTier).
  useDynamicPerfTier();
  const location = useLocation();
  const navigate = useNavigate();

  // `activeTab` é derivado da URL — URL é o source of truth.
  // Mantemos como state local apenas para compatibilidade com o código existente.
  const activeTab = useMemo<AppTab>(() => pathToTab(location.pathname), [location.pathname]);
  const setActiveTab = useCallback(
    (tab: AppTab) => {
      const target = tabToPath(tab);
      if (location.pathname !== target) {
        navigate(target);
      }
    },
    [navigate, location.pathname],
  );

  const [exploreCategory, setExploreCategory] = useState('Todos');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [scannerPartner, setScannerPartner] = useState<Partner | null>(null);

  /**
   * Abre/fecha o modal de parceiro mantendo a URL sincronizada.
   * - Abrir: navega para /parceiros/<id>, o useEffect de sync ativa o state.
   * - Fechar: navega -1 se a URL atual é de um parceiro; senão limpa o state.
   * Use esta função em vez de `setSelectedPartner` direto nos componentes filhos.
   */
  const openPartner = useCallback(
    (partner: Partner | null) => {
      if (partner) {
        const target = partnerDetailPath(partner.id);
        if (location.pathname !== target) {
          navigate(target);
        } else {
          setSelectedPartner(partner);
        }
      } else {
        if (getPartnerIdFromPath(location.pathname)) {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/');
          }
        } else {
          setSelectedPartner(null);
        }
      }
    },
    [navigate, location.pathname],
  );

  const handleNavigate = useCallback(
    (tab: string, params?: string) => {
      if (tab === 'home' || tab === 'explore' || tab === 'favorites' || tab === 'profile') {
        perfStart(`nav:${tab}`);
      }
      // Sixy AI só fica disponível quando a GEMINI API key está configurada (feature flag).
      // Em ambientes sem a key, o usuário cai em explore.
      if (tab === 'ai' && !isSixyAIEnabled()) {
        navigate(tabToPath('explore'));
        return;
      }

      if (tab === 'explore') {
        setExploreCategory(params || 'Todos');
      }
      navigate(tabToPath(tab as AppTab));
    },
    [navigate],
  );

  const [selectedCity, setSelectedCity] = useState('Porto Alegre');
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const splashStartedAtRef = useRef<number>(Date.now());
  const { profile, loading: authLoading, updateUserProfile, markTutorialSeen } = useAuth();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);

  // Mostra o tutorial fullscreen na primeira vez que um aluno/admin entra.
  // `tutorialSeenAt` é persistido em `users.tutorial_seen_at`. Reabre via Perfil > resetTutorial.
  // Companies têm dashboard próprio, sem tutorial.
  useEffect(() => {
    if (!profile) return;
    if (profile.role === 'company') return;
    if (profile.tutorialSeenAt) return;
    if (showSplash) return; // espera o splash sair pra não conflitar visualmente.
    setIsTutorialOpen(true);
  }, [profile, showSplash]);

  const handleCloseTutorial = useCallback(() => {
    setIsTutorialOpen(false);
    void markTutorialSeen();
  }, [markTutorialSeen]);
  const shouldLoadCoreData = Boolean(profile && profile.role !== 'company');
  const defaultAppSettings = useMemo<AppSettings>(
    () => ({
      mainImage: DEFAULT_CARD_IMAGE,
      availableCities: CITIES,
      availableCategories: [],
      loginOnboardingSlides: DEFAULT_LOGIN_ONBOARDING_SLIDES,
      partnerSupportWhatsapp: '+55 51 98643-0383',
    }),
    [],
  );

  const { favorites, toggleFavorite } = useFavorites();
  const cachedPartners = useMemo(
    () => readPublicCache<Partner[]>(PARTNERS_CACHE_KEY, PUBLIC_CACHE_MAX_AGE_MS) ?? undefined,
    [],
  );
  const cachedSettings = useMemo(
    () => readPublicCache<AppSettings>(SETTINGS_CACHE_KEY, PUBLIC_CACHE_MAX_AGE_MS) ?? undefined,
    [],
  );

  // Admin precisa ver parceiros desativados pra poder reativá-los; aluno só vê
  // os ativos. A queryKey carrega o flag pra que React Query trate como caches
  // separados (admin não polui o cache do aluno).
  const isAdminUser = profile?.role === 'admin';
  const {
    data: partners = [],
    isLoading: isLoadingPartners,
    isError: isPartnersError,
    refetch: refetchPartners,
  } = useQuery({
    queryKey: ['partners', { includeInactive: isAdminUser }],
    queryFn: async () => {
      perfStart('data:partners');
      const result = await api.getPartners({ includeInactive: isAdminUser });
      writePublicCache(PARTNERS_CACHE_KEY, result);
      perfEnd('data:partners', 'partners loaded');
      return result;
    },
    enabled: shouldLoadCoreData,
    initialData: cachedPartners,
  });

  const {
    data: appSettings = defaultAppSettings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ['appSettings'],
    queryFn: async () => {
      perfStart('data:settings');
      const result = await api.getAppSettings();
      writePublicCache(SETTINGS_CACHE_KEY, result);
      perfEnd('data:settings', 'settings loaded');
      return result;
    },
    enabled: shouldLoadCoreData,
    initialData: cachedSettings,
  });

  const [unauthRoute, setUnauthRoute] = useState<'login' | 'partner_join'>('login');

  const isLoading = authLoading || (shouldLoadCoreData && (isLoadingPartners || isLoadingSettings));

  const currentCityPartners = useMemo(() => {
    return partners.filter((p) => p.city === selectedCity);
  }, [partners, selectedCity]);

  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeTab]);

  // Sincroniza URL `/parceiros/<id>` <-> `selectedPartner`.
  // Permite deep link: refreshar com modal aberto reabre o modal.
  useEffect(() => {
    const partnerIdInUrl = getPartnerIdFromPath(location.pathname);
    if (partnerIdInUrl) {
      if (selectedPartner?.id === partnerIdInUrl) return;
      const found = partners.find((p) => p.id === partnerIdInUrl);
      if (found) {
        setSelectedPartner(found);
      } else if (partners.length > 0) {
        // Partners já carregou mas o id não existe — URL inválida (parceiro
        // removido/movido). Volta pra home silenciosamente.
        navigate('/', { replace: true });
      }
      // Se partners ainda está vazio (carregando), não faz nada — o effect
      // roda de novo quando `partners` chega.
    } else if (selectedPartner) {
      setSelectedPartner(null);
    }
  }, [location.pathname, partners, selectedPartner, navigate]);

  useEffect(() => {
    if (!isLoading && !showSplash) {
      perfEnd('app:start', 'app visible');
      perfLogOnce('app-visible', 'app visible');
    }
  }, [isLoading, showSplash]);

  // Marca o usuário logado no contexto do error tracker + analytics.
  useEffect(() => {
    if (profile) {
      setSentryUser({
        id: profile.uid,
        email: profile.email,
        role: profile.role,
      });
      setAnalyticsUser(profile.uid);
      setAuditContext({ userId: profile.uid, userEmail: profile.email });
    } else {
      setSentryUser(null);
      setAnalyticsUser(null);
      setAuditContext({});
    }
  }, [profile]);

  // Dispara `session_started` quando o app entra "carregado" pela 1a vez.
  // Útil pra contar DAU/MAU básico sem precisar de SDK externo.
  const sessionTrackedRef = useRef(false);
  useEffect(() => {
    if (isLoading || sessionTrackedRef.current) return;
    sessionTrackedRef.current = true;
    trackEvent('session_started', {
      role: profile?.role || 'guest',
      pwa_installed:
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(display-mode: standalone)').matches,
    });
  }, [isLoading, profile?.role]);

  useEffect(() => {
    if (isLoading) {
      splashStartedAtRef.current = Date.now();
      setShowSplash(true);
      return;
    }

    const elapsed = Date.now() - splashStartedAtRef.current;
    const remaining = Math.max(0, MIN_SPLASH_DURATION_MS - elapsed);
    let frameId = 0;
    const timeoutId = globalThis.setTimeout(() => {
      frameId = requestAnimationFrame(() => {
        globalThis.setTimeout(() => setShowSplash(false), SPLASH_EXIT_BUFFER_MS);
      });
    }, remaining);

    return () => {
      globalThis.clearTimeout(timeoutId);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || !profile) return;
    const preload = () => {
      void loadHomePage();
      void loadExplorePage();
      void loadFavoritesPage();
      void loadProfilePage();
      void loadPartnerModal();
      void loadCitySelectorModal();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const browserWindow = window as Window & {
        requestIdleCallback: (cb: () => void) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      const idleId = browserWindow.requestIdleCallback(preload);
      return () => {
        browserWindow.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId = globalThis.setTimeout(preload, 900);
    return () => globalThis.clearTimeout(timeoutId);
  }, [isLoading, profile]);

  useEffect(() => {
    if (!selectedPartner) return;

    const storageKey = `sixpass:partner-view:${selectedPartner.id}`;
    const now = Date.now();

    try {
      const lastTrackedAt = Number(sessionStorage.getItem(storageKey) || '0');
      if (lastTrackedAt && now - lastTrackedAt < PARTNER_VIEW_SESSION_WINDOW_MS) {
        return;
      }

      sessionStorage.setItem(storageKey, String(now));

      trackEvent('partner_viewed', {
        partner_id: selectedPartner.id,
        partner_name: selectedPartner.name,
        category: selectedPartner.category,
        city: selectedPartner.city,
      });

      void (async () => {
        await api.incrementPartnerViewCount(selectedPartner.id);

        if (profile?.role === 'student') {
          await updateUserProfile({
            offerViewsCount: (profile.offerViewsCount || 0) + 1,
          });
        }
      })();
    } catch (error) {
      console.error('Error tracking partner view', error);
    }
  }, [selectedPartner, profile, updateUserProfile]);

  // Páginas legais são acessíveis a qualquer momento, antes ou depois do login.
  // (boa prática LGPD: política de privacidade e termos sempre disponíveis)
  if (location.pathname === '/privacidade') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PrivacyPolicy />
      </Suspense>
    );
  }
  if (location.pathname === '/termos') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TermsOfService />
      </Suspense>
    );
  }

  // 404 — qualquer URL desconhecida cai numa tela amigável em vez de tela em branco.
  if (!isKnownPath(location.pathname)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <NotFound />
      </Suspense>
    );
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Página pública do parceiro `/p/<id>` — acessível logado e deslogado.
  // Renderiza ANTES de qualquer outra coisa pra permitir compartilhamento
  // externo (Insta, WhatsApp) abrir direto na landing.
  const publicPartnerId = getPartnerIdFromPublicPath(location.pathname);
  if (publicPartnerId) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PublicPartnerPage partnerId={publicPartnerId} />
      </Suspense>
    );
  }

  if (!profile) {
    if (unauthRoute === 'partner_join') {
      return (
        <div className="w-full max-w-md bg-[#030308] relative shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col min-h-[100svh] overflow-hidden mx-auto lg:min-h-[calc(100svh-5rem)] lg:max-h-[calc(100svh-5rem)] lg:rounded-[2.5rem] lg:border lg:border-white/10 lg:shadow-[0_50px_120px_rgba(0,0,0,0.7),inset_0_0_0_1px_rgba(255,255,255,0.03)] z-10">
          <button
            onClick={() => setUnauthRoute('login')}
            className="absolute left-6 z-50 text-white bg-white/10 px-4 py-2 rounded-xl hover:bg-white/20 transition-colors"
            style={{ top: 'calc(var(--safe-top, 0px) + 1.5rem)' }}
          >
            Voltar para Login
          </button>
          <div
            className="flex-1 overflow-y-auto hide-scrollbar z-10 scroll-smooth relative"
            style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 6rem)' }}
          >
            <PartnerJoin initialView="empresa" />
          </div>
        </div>
      );
    }
    return (
      <LoginFlow
        onLoginSuccess={(tab = 'home') => setActiveTab(tab as AppTab)}
        onNavigateToPartnerJoin={() => setUnauthRoute('partner_join')}
        appSettings={appSettings}
      />
    );
  }

  if (profile.role === 'company') {
    return (
      <>
        <div className="w-full max-w-md bg-[#030308] relative shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col min-h-[100svh] overflow-hidden mx-auto lg:min-h-[calc(100svh-5rem)] lg:max-h-[calc(100svh-5rem)] lg:rounded-[2.5rem] lg:border lg:border-white/10 lg:shadow-[0_50px_120px_rgba(0,0,0,0.7),inset_0_0_0_1px_rgba(255,255,255,0.03)] z-10">
          <CompanyDashboard appSettings={appSettings} />
        </div>
        <AnimatePresence>{showSplash ? <LoadingScreen overlay /> : null}</AnimatePresence>
      </>
    );
  }

  // Rota /admin: full-width workspace, sem o frame mobile.
  // Renderiza Admin direto, ocupando todo o espaço útil.
  if (activeTab === 'admin') {
    return (
      <>
        <div className="relative z-10 w-full min-h-[100svh] bg-[#030308]">
          <Suspense fallback={<AdminSkeleton />}>
            <Admin
              onNavigate={handleNavigate}
              onDataChanged={() => {
                void queryClient.invalidateQueries({ queryKey: ['partners'] });
                void refetchPartners();
              }}
              partners={partners}
              appSettings={appSettings}
              onSettingsChanged={() => {
                void queryClient.invalidateQueries({ queryKey: ['appSettings'] });
                void refetchSettings();
              }}
            />
          </Suspense>
        </div>
        <AnimatePresence>{showSplash ? <LoadingScreen overlay /> : null}</AnimatePresence>
      </>
    );
  }

  return (
    <>
      <div className="w-full max-w-md bg-[#030308] relative shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col min-h-[100svh] overflow-hidden mx-auto lg:min-h-[calc(100svh-5rem)] lg:max-h-[calc(100svh-5rem)] lg:rounded-[2.5rem] lg:border lg:border-white/10 lg:shadow-[0_50px_120px_rgba(0,0,0,0.7),inset_0_0_0_1px_rgba(255,255,255,0.03)] z-10">
        {/*
         * Blurs ambiente. `blur-[100px]` é muito caro pro WebKit no iPhone
         * durante scroll — reduzimos pra `blur-[60px]` e forçamos GPU
         * compositing com `transform: translateZ(0)` pra estabilizar FPS.
         */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden" style={{ transform: 'translateZ(0)' }}>
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[50%] rounded-full bg-pink-600/10 blur-[60px] mix-blend-screen"></div>
          <div className="absolute top-[20%] -right-[20%] w-[60%] h-[60%] rounded-full bg-blue-600/10 blur-[60px] mix-blend-screen"></div>
          <div className="absolute -bottom-[10%] left-[10%] w-[80%] h-[40%] rounded-full bg-purple-600/10 blur-[60px] mix-blend-screen"></div>
        </div>

        <div
          ref={contentRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden hide-scrollbar z-10 scroll-smooth relative"
          style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 6rem)' }}
        >
          {['home', 'explore', 'favorites', 'profile'].includes(activeTab) ? (
            <div className="relative w-full min-h-full">
              <Suspense fallback={activeTab === 'home' ? <HomeSkeleton /> : null}>
                <div
                  key={`home-${activeTab === 'home' ? 'active' : 'idle'}`}
                  className={
                    activeTab === 'home' ? 'block tab-enter-active' : 'hidden'
                  }
                >
                  <Home
                    isActive={activeTab === 'home'}
                    onNavigate={handleNavigate}
                    onSelectPartner={openPartner}
                    partners={currentCityPartners}
                    selectedCity={selectedCity}
                    onOpenCitySelector={() => setIsCityModalOpen(true)}
                    appSettings={appSettings}
                    favoriteIds={favorites}
                    onToggleFavorite={toggleFavorite}
                  />
                </div>
              </Suspense>

              <Suspense fallback={activeTab === 'explore' ? <ExploreSkeleton /> : null}>
                <div
                  key={`explore-${activeTab === 'explore' ? 'active' : 'idle'}`}
                  className={
                    activeTab === 'explore' ? 'block tab-enter-active' : 'hidden'
                  }
                >
                  <Explore
                    isActive={activeTab === 'explore'}
                    onSelectPartner={openPartner}
                    partners={currentCityPartners}
                    selectedCity={selectedCity}
                    favoriteIds={favorites}
                    onToggleFavorite={toggleFavorite}
                    initialCategory={exploreCategory}
                    onNavigate={handleNavigate}
                    onOpenCitySelector={() => setIsCityModalOpen(true)}
                    isError={isPartnersError}
                    onRetry={() => void refetchPartners()}
                  />
                </div>
              </Suspense>

              <Suspense fallback={activeTab === 'favorites' ? <FavoritesSkeleton /> : null}>
                <div
                  key={`favorites-${activeTab === 'favorites' ? 'active' : 'idle'}`}
                  className={
                    activeTab === 'favorites' ? 'block tab-enter-active' : 'hidden'
                  }
                >
                  <Favorites
                    onSelectPartner={openPartner}
                    partners={partners}
                    favoriteIds={favorites}
                    onToggleFavorite={toggleFavorite}
                    onNavigateToExplore={() => handleNavigate('explore')}
                  />
                </div>
              </Suspense>

              <Suspense fallback={activeTab === 'profile' ? <ProfileSkeleton /> : null}>
                <div
                  key={`profile-${activeTab === 'profile' ? 'active' : 'idle'}`}
                  className={
                    activeTab === 'profile' ? 'block tab-enter-active' : 'hidden'
                  }
                >
                  <Profile
                    isActive={activeTab === 'profile'}
                    onNavigate={handleNavigate}
                    partners={partners}
                  />
                </div>
              </Suspense>
            </div>
          ) : (
            <motion.div
              key={activeTab}
              initial={reducedMotion ? false : { opacity: 0, y: 8 }}
              animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className="w-full min-h-full"
            >
              <Suspense fallback={<FavoritesSkeleton />}>
                {activeTab === 'partner' && <PartnerJoin initialView="empresa" />}
                {activeTab === 'indication' && <PartnerJoin initialView="formando" />}
              </Suspense>
            </motion.div>
          )}
        </div>

        <BottomNav
          activeTab={activeTab}
          onNavigate={handleNavigate}
          onOpenScanner={() => setIsScannerOpen(true)}
        />

        {isCityModalOpen && (
          <Suspense fallback={null}>
            <CitySelectorModal
              cities={CITIES}
              selectedCity={selectedCity}
              onSelect={(city) => {
                setSelectedCity(city);
                setIsCityModalOpen(false);
              }}
              onClose={() => setIsCityModalOpen(false)}
            />
          </Suspense>
        )}

        {selectedPartner && (
          <Suspense fallback={null}>
            <PartnerModal
              partner={selectedPartner}
              onClose={() => openPartner(null)}
              onOpenScanner={(partner) => {
                setScannerPartner(partner || null);
                openPartner(null);
                setIsScannerOpen(true);
              }}
            />
          </Suspense>
        )}

        {isScannerOpen && (
          <Suspense fallback={<ScannerSkeleton />}>
            <Scanner
              onClose={() => {
                setIsScannerOpen(false);
                setScannerPartner(null);
              }}
              partners={partners}
              initialPartner={scannerPartner}
            />
          </Suspense>
        )}
      </div>
      <GuidedTour
        open={isTutorialOpen}
        onClose={handleCloseTutorial}
        cardImageUrl={appSettings.mainImage}
      />
      {/* FAB removido por decisão de design — Sixy agora aparece apenas em
          lugares contextualmente relevantes (tour, achievement, easter egg,
          pull-to-refresh, daily quote, páginas de erro). Acesso ao chat
          Sixy AI continua via tab dedicada. */}
      <AnimatePresence>{showSplash ? <LoadingScreen overlay /> : null}</AnimatePresence>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AppShell />
      </ToastProvider>
    </QueryClientProvider>
  );
}

/**
 * Shell visual do app. Decide entre:
 *   - Rota /admin → layout full-width pra desktop (sem frame de phone)
 *   - Demais rotas → frame de phone centralizado no desktop, mobile normal
 */
function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div
      className={`relative min-h-[100svh] w-full bg-[#030308] text-slate-100 flex justify-center font-sans selection:bg-pink-500 selection:text-white overflow-hidden ${
        isAdminRoute ? '' : 'lg:items-center lg:py-10'
      }`}
    >
      {/* Ambient background + branding aparecem só nas rotas NÃO-admin
          (admin é workspace, não preview). */}
      {!isAdminRoute && (
        <>
          <div className="hidden lg:block absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute -top-[15%] -left-[10%] h-[60%] w-[55%] rounded-full bg-pink-600/10 blur-[140px] mix-blend-screen"
              style={{ transform: 'translateZ(0)' }}
            />
            <div
              className="absolute top-[10%] -right-[12%] h-[55%] w-[50%] rounded-full bg-blue-600/10 blur-[140px] mix-blend-screen"
              style={{ transform: 'translateZ(0)' }}
            />
            <div
              className="absolute -bottom-[20%] left-[15%] h-[55%] w-[70%] rounded-full bg-purple-600/10 blur-[140px] mix-blend-screen"
              style={{ transform: 'translateZ(0)' }}
            />
          </div>

          <div className="hidden lg:flex absolute top-8 left-8 pointer-events-none items-center gap-3 z-10">
            <img
              src="/six-logo.png"
              alt="Six Formaturas"
              loading="lazy"
              decoding="async"
              className="h-8 w-auto opacity-70"
            />
            <div className="text-xs text-slate-500 font-medium">
              App oficial do Six Pass
            </div>
          </div>
          <div className="hidden lg:block absolute bottom-8 right-8 pointer-events-none z-10">
            <p className="text-[10px] text-slate-600 uppercase tracking-[0.2em] font-bold">
              Six Pass · App Mobile
            </p>
          </div>
        </>
      )}

      <ConfirmProvider>
        <ConciergeProvider>
          <AppContent />
          <UpdatePrompt />
          <InstallBanner />
          <OfflineBanner />
          <AvatarOnboarding />
          <DailyBonusModal onNavigateToCheckin={() => navigate('/explorar')} />
          <PushPromptAfterCheckin />
        </ConciergeProvider>
      </ConfirmProvider>
    </div>
  );
}
